import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { isDateAvailable } from './calendarBlocks';

export interface Appointment {
  id: string;
  lead_id: string | null;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  date: string;
  time_start: string | null;
  status: 'requested' | 'confirmed' | 'rescheduled' | 'completed' | 'canceled' | 'no_show';
  notes: string | null;
  created_at: string;
  city?: string | null;
  event_type?: string | null;
  event_date?: string | null;
  appointment_type?: string | null;
  rescheduled_from?: string | null;
  rescheduled_at?: string | null;
}

export interface CreateAppointmentData {
  lead_id?: string;
  client_name: string;
  client_email?: string;
  client_phone?: string;
  date: string;
  time_start?: string;
  status?: 'requested' | 'confirmed' | 'rescheduled' | 'completed' | 'canceled' | 'no_show';
  notes?: string;
  city?: string;
  event_type?: string;
  event_date?: string;
  appointment_type?: string;
  rescheduled_from?: string;
}

export async function getAppointments(): Promise<Appointment[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select('*')
    .order('date', { ascending: true })
    .order('time_start', { ascending: true });

  if (error) throw error;
  return data || [];
}

// Obtener solo citas pendientes (para notificaciones)
export async function getPendingAppointments(): Promise<Appointment[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select('*')
    .in('status', ['requested', 'confirmed', 'rescheduled'])
    .order('date', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createAppointment(appointmentData: CreateAppointmentData): Promise<Appointment> {
  // Validar que la fecha no esté bloqueada
  const dateAvailable = await isDateAvailable(appointmentData.date);
  if (!dateAvailable) {
    throw new Error('Esta fecha está bloqueada y no se pueden crear citas');
  }

  // Validar que no exista otra cita en la misma fecha (1 cita por día)
  const { data: existingAppointments, error: checkError } = await supabase
    .from('appointments')
    .select('id')
    .eq('date', appointmentData.date)
    .neq('status', 'canceled');

  if (checkError) throw checkError;

  if (existingAppointments && existingAppointments.length > 0) {
    throw new Error('Ya existe una cita programada para esta fecha');
  }

  // Crear la cita vía API
  try {
    const response = await apiFetch('/appointments', {
      method: 'POST',
      body: JSON.stringify({ ...appointmentData, status: appointmentData.status || 'requested' })
    });
    
    return response.appointment;
  } catch (error: any) {
    console.error('Error al crear cita:', error);
    throw new Error(error.message || 'Error al crear la cita');
  }
}

// Wrapper seguro con manejo de errores estandarizado
export interface AppointmentResult {
  ok: boolean;
  data?: Appointment;
  error?: string;
}

export async function createAppointmentSafe(appointmentData: CreateAppointmentData): Promise<AppointmentResult> {
  try {
    console.log('CREATE_APPOINTMENT_START', appointmentData);
    
    const appointment = await createAppointment(appointmentData);
    
    console.log('CREATE_APPOINTMENT_SUCCESS', appointment);
    return { ok: true, data: appointment };
  } catch (err: any) {
    console.error('CREATE_APPOINTMENT_ERROR', {
      message: err.message,
      code: err.code,
      details: err.details,
      hint: err.hint,
      appointmentData,
    });
    
    return { 
      ok: false, 
      error: err.message || 'Error desconocido al crear la cita' 
    };
  }
}

// Confirmar cita
export async function confirmAppointment(id: string): Promise<void> {
  try {
    await apiFetch(`/appointments/${id}/confirm`, {
      method: 'POST'
    });
  } catch (error: any) {
    console.error('Error al confirmar cita:', error);
    throw new Error(error.message || 'Error al confirmar la cita');
  }
}

// Reprogramar cita
export async function rescheduleAppointment(id: string, newDate: string, newTime?: string): Promise<void> {
  try {
    await apiFetch(`/appointments/${id}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ 
        date: newDate,
        time_start: newTime || null,
        rescheduled_from: id,
        rescheduled_at: new Date().toISOString()
      })
    });
  } catch (error: any) {
    console.error('Error al reprogramar cita:', error);
    throw new Error(error.message || 'Error al reprogramar la cita');
  }
}

// Marcar cita como completada
export async function completeAppointment(id: string): Promise<void> {
  try {
    await apiFetch(`/appointments/${id}/complete`, {
      method: 'POST'
    });
  } catch (error: any) {
    console.error('Error al completar cita:', error);
    throw new Error(error.message || 'Error al completar la cita');
  }
}

// Marcar como no asistió
export async function markAppointmentNoShow(id: string): Promise<void> {
  try {
    await apiFetch(`/appointments/${id}/no-show`, {
      method: 'POST'
    });
  } catch (error: any) {
    console.error('Error al marcar no show:', error);
    throw new Error(error.message || 'Error al marcar como no show');
  }
}

export async function updateAppointmentStatus(
  id: string,
  status: 'requested' | 'confirmed' | 'rescheduled' | 'completed' | 'canceled' | 'no_show'
): Promise<void> {
  try {
    await apiFetch(`/appointments/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  } catch (error: any) {
    console.error('Error al actualizar estado de cita:', error);
    throw new Error(error.message || 'Error al actualizar el estado');
  }
}

export async function deleteAppointment(id: string): Promise<void> {
  try {
    await apiFetch(`/appointments/${id}`, {
      method: 'DELETE'
    });
  } catch (error: any) {
    console.error('Error al eliminar cita:', error);
    throw new Error(error.message || 'Error al eliminar la cita');
  }
}
